import React, { useState, useEffect } from 'react'
import {
    AllocationStatusCounts,
    AllocationStatusFilters as FilterType,
    AllocationStatusPageMetaProps,
    Area,
    Event,
    Shift
} from '../data/types'
import { ChevronDownIcon, ChevronUpIcon, FilterIcon, SearchIcon, XIcon } from '../icons'
import { Button } from './ui'
import { CheckIcon } from 'flowbite-react'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'

type Props = {
    meta: AllocationStatusPageMetaProps
    filters: FilterType
    counts: AllocationStatusCounts
    events: Event[]
    areas: Area[]
    shifts: Shift[]
    onFiltersChange: (filters: FilterType) => void
    onClearFilters: () => void
}


function TriState(props: { value: boolean | null; onClick: () => void; label: string; yes?: string | null; no?: string | null }) {
    const { value, onClick, label, yes = "Yes", no = "No" } = props;
    let borderColor = "border-gray-400";
    let bgColor = "bg-white dark:bg-gray-700";
    let icon = null;

    if (value === true) {
        borderColor = "border-success-500";
        bgColor = "bg-success-500/20";
        icon = <CheckIcon className="w-3 h-3 text-success-500" />;
    } else if (value === false) {
        borderColor = "border-danger-500";
        bgColor = "bg-danger-500/20";
        icon = <XIcon className="w-3 h-3 text-danger-500" />;
    }

    return (
        <label
            className="flex items-center gap-3 cursor-pointer select-none group transition"
            onClick={onClick}
        >
            <div
                className={`relative w-5 h-5 rounded flex items-center justify-center border-2 ${borderColor} ${bgColor} transition-colors duration-150 group-hover:shadow-md`}
            >
                {icon}
            </div>
            <span className="text-gray-800 dark:text-gray-200 font-medium">
                {label}
                <span className="ms-2 text-xs text-gray-500 dark:text-gray-400">
                    {value === null ? "(Any)" : value ? `(${yes})` : `(${no})`}
                </span>
            </span>
        </label>
    );
}


export const AllocationStatusFilters = (props: Props) => {
    const {
        meta,
        filters,
        counts,
        events,
        areas,
        shifts,
        onFiltersChange,
        onClearFilters,
    } = props

    const [isExpanded, setIsExpanded] = useState(false)
    const [localFilters, setLocalFilters] = useState<FilterType>(filters)

    const handleStatusClick = (status: 'pending' | 'accepted' | 'declined' | null) => {
        const newFilters = { ...localFilters, status }
        setLocalFilters(newFilters)
        onFiltersChange(newFilters)
    }

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleInputChange = (field: keyof FilterType, value: any) => {
        const newFilters = { ...localFilters, [field]: value }

        // When event changes, reset area and shift
        if (field === 'event_id') {
            newFilters.area_id = null
            newFilters.shift_id = null
        }
        // When area changes, reset shift
        if (field === 'area_id') {
            newFilters.shift_id = null
        }

        setLocalFilters(newFilters)

        // Immediately trigger filter change for cascading dropdowns to load
        if (field === 'event_id' || field === 'area_id') {
            onFiltersChange(newFilters)
        }
    }

    const handleSearch = () => {
        onFiltersChange(localFilters)
    }

    const handleClear = () => {
        const clearedFilters: FilterType = {
            status: null,
            event_id: null,
            area_id: null,
            shift_id: null,
            volunteer_search: '',
            decline_reason: [],
            date_from: '',
            date_to: '',
            offered_as_tkn: null,
            min_pending_count: null,
            max_pending_count: null,
            min_accepted_count: null,
            max_accepted_count: null,
            min_declined_count: null,
            max_declined_count: null,
            count_filter_event_ids: [],
            sort_by: 'updated_at',
            sort_dir: 'desc',
            startTime: '',
            endTime: '',
            review: false
        }
        setLocalFilters(clearedFilters)
        onClearFilters()
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleDeclineReasonToggle = (reason: string) => {
        const currentReasons = localFilters.decline_reason || []
        const newReasons = currentReasons.includes(reason)
            ? currentReasons.filter(r => r !== reason)
            : [...currentReasons, reason]
        handleInputChange('decline_reason', newReasons)
    }

    // Status badge styling
    const getStatusBadgeClass = (status: 'pending' | 'accepted' | 'declined' | null, isActive: boolean) => {
        const baseClass = 'px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all'
        if (isActive) {
            switch (status) {
                case 'pending':
                    return `${baseClass} bg-warn-500 text-white`
                case 'accepted':
                    return `${baseClass} bg-success-500 text-white`
                case 'declined':
                    return `${baseClass} bg-danger-500 text-white`
                default:
                    return `${baseClass} bg-primary-500 text-white`
            }
        }
        switch (status) {
            case 'pending':
                return `${baseClass} bg-warn-100 text-warn-700 dark:bg-warn-900 dark:text-warn-300 hover:bg-warn-200 dark:hover:bg-warn-800`
            case 'accepted':
                return `${baseClass} bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300 hover:bg-success-200 dark:hover:bg-success-800`
            case 'declined':
                return `${baseClass} bg-danger-100 text-danger-700 dark:bg-danger-900 dark:text-danger-300 hover:bg-danger-200 dark:hover:bg-danger-800`
            default:
                return `${baseClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`
        }
    }

    const handleReviewToggle = () => {
        let newValue: boolean | null;
        const currentValue = localFilters.review;

        if (currentValue === null) {
            newValue = true;
        } else if (currentValue === true) {
            newValue = false;
        } else {
            newValue = null;
        }

        // Update local state just like other inputs
        setLocalFilters(prev => ({ ...prev, review: newValue }));
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
            {/* Status filter badges */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 me-2">
                    {meta.statusFilterLabel}:
                </span>
                <button
                    className={getStatusBadgeClass(null, localFilters.status === null)}
                    onClick={() => handleStatusClick(null)}
                >
                    {meta.allLabel} ({counts.total})
                </button>
                <button
                    className={getStatusBadgeClass('pending', localFilters.status === 'pending')}
                    onClick={() => handleStatusClick('pending')}
                >
                    {meta.pendingLabel} ({counts.pending})
                </button>
                <button
                    className={getStatusBadgeClass('accepted', localFilters.status === 'accepted')}
                    onClick={() => handleStatusClick('accepted')}
                >
                    {meta.acceptedLabel} ({counts.accepted})
                </button>
                <button
                    className={getStatusBadgeClass('declined', localFilters.status === 'declined')}
                    onClick={() => handleStatusClick('declined')}
                >
                    {meta.declinedLabel} ({counts.declined})
                </button>
            </div>

            {/* Quick search bar */}
            <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <SearchIcon className="absolute start-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={meta.volunteerSearchPlaceholder}
                        value={localFilters.volunteer_search}
                        onChange={(e) => handleInputChange('volunteer_search', e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="w-full ps-10 pe-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                </div>
                <Button onClick={handleSearch} size="sm">
                    {meta.searchButton}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <FilterIcon className="w-4 h-4 me-2" />
                    {meta.filtersTitle}
                    {isExpanded ? (
                        <ChevronUpIcon className="w-4 h-4 ms-2" />
                    ) : (
                        <ChevronDownIcon className="w-4 h-4 ms-2" />
                    )}
                </Button>
            </div>

            {/* Expanded filters */}
            {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                    {/* Event/Area/Shift cascade */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.eventFilterLabel}
                            </label>
                            <select
                                value={localFilters.event_id || ''}
                                onChange={(e) => handleInputChange('event_id', e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">{meta.selectEventPlaceholder}</option>
                                {sortByName(events).map(event => (
                                    <option key={event.id} value={event.id}>{event.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.areaFilterLabel}
                            </label>
                            <select
                                value={localFilters.area_id || ''}
                                onChange={(e) => handleInputChange('area_id', e.target.value ? Number(e.target.value) : null)}
                                disabled={!localFilters.event_id}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                                <option value="">{meta.selectAreaPlaceholder}</option>
                                {sortByName(areas).map(area => (
                                    <option key={area.id} value={area.id}>{area.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.shiftFilterLabel}
                            </label>
                            <select
                                value={localFilters.shift_id || ''}
                                onChange={(e) => handleInputChange('shift_id', e.target.value ? Number(e.target.value) : null)}
                                disabled={!localFilters.area_id}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                                <option value="">{meta.selectShiftPlaceholder}</option>
                                {sortShifts(shifts).map(shift => (
                                    <option key={shift.id} value={shift.id}>
                                        {getShiftDisplayName(shift)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Date range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.dateFromLabel}
                            </label>
                            <input
                                type="date"
                                value={localFilters.date_from}
                                onChange={(e) => handleInputChange('date_from', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.dateToLabel}
                            </label>
                            <input
                                type="date"
                                value={localFilters.date_to}
                                onChange={(e) => handleInputChange('date_to', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>

                    {/* Start Time / End Time */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.startTimeLabel}
                            </label>
                            <input
                                type="time"
                                value={localFilters.startTime}
                                onChange={(e) => handleInputChange('startTime', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.endTimeLabel}
                            </label>
                            <input
                                type="time"
                                value={localFilters.endTime}
                                onChange={(e) => handleInputChange('endTime', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>

                    {/* Decline reasons (only show when status is declined) */}
                    {localFilters.status === 'declined' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {meta.declineReasonFilterLabel}
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {meta.declineReasons.map((reason, index) => {
                                    const isSelected = localFilters.decline_reason.includes(reason)
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => handleDeclineReasonToggle(reason)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${isSelected
                                                ? 'bg-danger-500 text-white'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            {reason.length > 50 ? reason.substring(0, 50) + '...' : reason}
                                            {isSelected && <XIcon className="w-3 h-3 ms-1 inline" />}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* TKN filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {meta.offeredAsTknLabel}
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleInputChange('offered_as_tkn', null)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${localFilters.offered_as_tkn === null
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                                    }`}
                            >
                                {meta.allLabel}
                            </button>
                            <button
                                onClick={() => handleInputChange('offered_as_tkn', true)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${localFilters.offered_as_tkn === true
                                    ? 'bg-success-500 text-white'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                                    }`}
                            >
                                Yes
                            </button>
                            <button
                                onClick={() => handleInputChange('offered_as_tkn', false)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${localFilters.offered_as_tkn === false
                                    ? 'bg-danger-500 text-white'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                                    }`}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {/* Volunteer count filters */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {meta.volunteerCountsTitle}
                        </label>

                        {/* Event selection for count filtering */}
                        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                {meta.volunteerCountsEventsLabel}
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => handleInputChange('count_filter_event_ids', [])}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${localFilters.count_filter_event_ids.length === 0
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                                        }`}
                                >
                                    {meta.volunteerCountsEventsAllLabel}
                                </button>
                                {sortByName(events).map(event => (
                                    <button
                                        key={event.id}
                                        onClick={() => {
                                            const currentIds = localFilters.count_filter_event_ids || []
                                            const newIds = currentIds.includes(event.id)
                                                ? currentIds.filter(id => id !== event.id)
                                                : [...currentIds, event.id]
                                            handleInputChange('count_filter_event_ids', newIds)
                                        }}
                                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${localFilters.count_filter_event_ids.includes(event.id)
                                            ? 'bg-primary-500 text-white'
                                            : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                                            }`}
                                    >
                                        {event.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.minPendingCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.min_pending_count ?? ''}
                                    onChange={(e) => handleInputChange('min_pending_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-warn-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.maxPendingCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.max_pending_count ?? ''}
                                    onChange={(e) => handleInputChange('max_pending_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-warn-500"
                                    placeholder="∞"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.minAcceptedCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.min_accepted_count ?? ''}
                                    onChange={(e) => handleInputChange('min_accepted_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-success-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.maxAcceptedCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.max_accepted_count ?? ''}
                                    onChange={(e) => handleInputChange('max_accepted_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-success-500"
                                    placeholder="∞"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.minDeclinedCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.min_declined_count ?? ''}
                                    onChange={(e) => handleInputChange('min_declined_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-danger-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {meta.maxDeclinedCountLabel}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={localFilters.max_declined_count ?? ''}
                                    onChange={(e) => handleInputChange('max_declined_count', e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-danger-500"
                                    placeholder="∞"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <TriState
                            value={localFilters.review}
                            onClick={handleReviewToggle}
                            label="Review Status"
                            yes="Reviewed"
                            no="Not Reviewed"
                        />
                    </div>

                    {/* Action buttons */}
                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="outline" size="sm" onClick={handleClear}>
                            {meta.clearFiltersButton}
                        </Button>
                        <Button size="sm" onClick={handleSearch}>
                            {meta.searchButton}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
